// API client for Verso Cloud dashboard.
//
// In dev the Vite proxy makes everything same-origin. In production, the
// dashboard is on <slug>.verso-cloud.io and the API is on api.verso-cloud.io
// — set VITE_API_BASE at build time for cross-origin calls.

import { recordBreadcrumb } from './breadcrumbs.js';

const API_BASE = import.meta.env.VITE_API_BASE || window.location.origin;

const REQUEST_TIMEOUT_MS = 10_000;

// Callers can override the default per-request when they know the endpoint
// is long-running (e.g. Commio / GDMS imports that walk paginated upstream
// APIs). Ten seconds isn't enough for a full inventory sync.
const IMPORT_TIMEOUT_MS = 120_000;
export { IMPORT_TIMEOUT_MS };

// A PROVISIONING RUN IS MINUTES, NOT SECONDS, and it writes to live systems.
//
// Building line 136 on 2026-08-13 was 28 steps — fourteen FreePBX calls, ten GDMS calls,
// three at the carrier — and the browser gave up on it after ten seconds. The run itself
// finished perfectly: `line.provision` in the audit log, all 28 steps ok, verification
// confirmed, external systems commio/pbx/gdms. Only the listener left.
//
// The operator was told "Request timed out — please try again", so the advice on screen was
// to repeat 28 live external writes that had already landed. Aborting the fetch does not
// cancel the Worker, and it never could: there is nothing to cancel through.
const RUN_TIMEOUT_MS = 300_000;
export { RUN_TIMEOUT_MS };

// Session JWT — stored in memory only, never localStorage/cookies.
// Cleared on tab close; SPA re-mints on next mount via /auth/session.
let sessionToken = null;

export function setSessionToken(token) {
  sessionToken = token;
}

export function getSessionToken() {
  return sessionToken;
}

// Token for WebSocket auth. Browsers can't send custom headers on a WS
// handshake, so callers append `?token=` to the WS URL. The Worker's
// withAuth accepts either the Authorization header or a `token` query param.
export function getWsToken() {
  if (sessionToken) return sessionToken;
  // Fallback: Cloudflare Access JWT from cookie (before /auth/session is called).
  const cfMatch = document.cookie.match(/CF_Authorization=([^;]+)/);
  if (cfMatch) return cfMatch[1];
  return null;
}

async function request(method, path, { params, body, timeoutMs } = {}) {
  const url = new URL(`/api/v1${path}`, API_BASE);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value != null) url.searchParams.set(key, value);
    }
  }

  const isCrossOrigin = url.origin !== window.location.origin;

  // A FormData BODY MUST NOT BE TOUCHED.
  //
  // This client set `Content-Type: application/json` and `JSON.stringify(body)` unconditionally, so
  // a FormData was stringified to `{}` — and the server, reasonably, refused: "A multipart body with
  // an `audio` part is required". That is what broke the operator's recording upload on 2026-08-27.
  //
  // The browser sets `multipart/form-data` AND the boundary itself; declaring the header by hand
  // produces a request with no boundary, which fails in a different and less obvious way. So the
  // rule is: leave both alone and let the browser do it.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;

  const options = {
    method,
    headers: isForm ? {} : { 'Content-Type': 'application/json' },
    // Cross-origin requests need `credentials: 'include'` so the browser sends
    // cookies to the API subdomain (CF_Authorization is scoped to the dashboard
    // subdomain; without credentials the API can't verify the CF Access session).
    credentials: isCrossOrigin ? 'include' : 'same-origin',
  };

  if (sessionToken) {
    options.headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  // For cross-origin requests, the CF_Authorization cookie won't be sent
  // automatically (scoped to the dashboard subdomain, not the API domain).
  // Read from cookie and pass as a header so the Worker can validate.
  if (isCrossOrigin) {
    const cfMatch = document.cookie.match(/CF_Authorization=([^;]+)/);
    if (cfMatch) {
      options.headers['Cf-Access-Jwt-Assertion'] = cfMatch[1];
    }
  }

  if (body) {
    options.body = isForm ? body : JSON.stringify(body);
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs ?? REQUEST_TIMEOUT_MS);
  options.signal = controller.signal;

  let res;
  try {
    res = await fetch(url, options);
  } catch (err) {
    clearTimeout(timeout);
    if (err.name === 'AbortError') {
      const timeoutErr = new Error('Request timed out — please try again');
      timeoutErr.status = 0;
      // FLAGGED, so a caller that started something can tell "we stopped waiting" from
      // "it failed". For a read those are the same thing; for a run that writes to a PBX,
      // a carrier and a customer's handsets they are not remotely the same, and only the
      // caller knows which it was. See RUN_TIMEOUT_MS above.
      timeoutErr.timedOut = true;
      throw timeoutErr;
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }

  // Support-ticket breadcrumbs: method + path + status ONLY (never bodies —
  // no PII/secrets can leak into a ticket). Errors always; successful
  // mutations too, so the trail shows what the user just did.
  if (!res.ok) {
    recordBreadcrumb({ kind: 'api_error', method, path, status: res.status });
  } else if (method !== 'GET') {
    recordBreadcrumb({ kind: 'api', method, path, status: res.status });
  }

  // Set by the Worker's proxy layer when returning cached data because the
  // upstream verso-agent was unreachable. UI can render stale-warning badges.
  const stale = res.headers.get('X-Verso-Stale') === 'true';

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(errBody.error || `API ${method} ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = errBody;
    throw err;
  }

  if (res.status === 204) return null;
  const data = await res.json();
  if (stale) data._stale = true;
  return data;
}

// Binary GET — same auth/cookie handling as request(), but returns a Blob
// (voicemail/greeting audio for the in-page <audio> player). No JSON parse,
// no stale-header handling; errors still surface the JSON error body.
async function requestBlob(path) {
  const url = new URL(`/api/v1${path}`, API_BASE);
  const isCrossOrigin = url.origin !== window.location.origin;
  const options = {
    method: 'GET',
    headers: {},
    credentials: isCrossOrigin ? 'include' : 'same-origin',
  };
  if (sessionToken) options.headers['Authorization'] = `Bearer ${sessionToken}`;
  if (isCrossOrigin) {
    const cfMatch = document.cookie.match(/CF_Authorization=([^;]+)/);
    if (cfMatch) options.headers['Cf-Access-Jwt-Assertion'] = cfMatch[1];
  }
  const res = await fetch(url, options);
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(errBody.error || `API GET ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = errBody;
    throw err;
  }
  return res.blob();
}

// NDJSON POST — one callback per line, as the server writes them.
//
// Kevin, 2026-08-20: "as each task is run, show progress like running, verifying, etc..." A build
// takes about ten seconds across three external systems, and `request()` cannot report anything until
// the last byte. This reads the body as it arrives and hands each line to `onEvent`.
//
// NO TIMEOUT, deliberately. `request()` aborts a run at RUN_TIMEOUT_MS because a silent wait is
// indistinguishable from a hang — but a stream that is still emitting lines is visibly alive, and
// aborting mid-build is the one thing worse than waiting. The stream ending IS the end.
//
// Errors arrive as a line, not a status: by the time the first byte is written the status is already
// 200. The caller decides what a `type: 'error'` line means; this only guarantees delivery.
async function requestStream(path, body, onEvent) {
  const url = new URL(`/api/v1${path}`, API_BASE);
  const isCrossOrigin = url.origin !== window.location.origin;
  const headers = { 'Content-Type': 'application/json' };
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;
  if (isCrossOrigin) {
    const cfMatch = document.cookie.match(/CF_Authorization=([^;]+)/);
    if (cfMatch) headers['Cf-Access-Jwt-Assertion'] = cfMatch[1];
  }

  const res = await fetch(url, {
    method: 'POST',
    headers,
    credentials: isCrossOrigin ? 'include' : 'same-origin',
    body: JSON.stringify(body ?? {}),
  });
  recordBreadcrumb({ kind: res.ok ? 'api' : 'api_error', method: 'POST', path, status: res.status });

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(errBody.error || `API POST ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = errBody;
    throw err;
  }
  if (!res.body) {
    // No stream to read (a proxy collapsed it, or a browser without streaming bodies). The response
    // is still NDJSON, so parse what we have rather than failing a run that already happened.
    const text = await res.text();
    for (const line of text.split('\n')) {
      if (line.trim()) onEvent(JSON.parse(line));
    }
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // A chunk boundary can land mid-line; keep the tail until its newline arrives.
    const lines = buf.split('\n');
    buf = lines.pop() ?? '';
    for (const line of lines) {
      if (!line.trim()) continue;
      // One malformed line must not discard the rest of the run's narration.
      try { onEvent(JSON.parse(line)); } catch { /* ignore a partial or junk line */ }
    }
  }
  if (buf.trim()) {
    try { onEvent(JSON.parse(buf)); } catch { /* ignore a trailing partial */ }
  }
}

export const api = {
  postStream: (path, body, onEvent) => requestStream(path, body, onEvent),
  get:    (path, params, opts) => request('GET',    path, { params,       ...opts }),
  getBlob: (path) => requestBlob(path),
  post:   (path, body, params, opts) => request('POST',   path, { params, body, ...opts }),
  put:    (path, body, params, opts) => request('PUT',    path, { params, body, ...opts }),
  patch:  (path, body, params, opts) => request('PATCH',  path, { params, body, ...opts }),
  delete: (path, body, params, opts) => request('DELETE', path, { params, body, ...opts }),
};
